import { max, find, isEqual, isEmpty, omit } from 'lodash';
import { observable, makeObservable, action, computed } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import { EngineTab } from '@/constants';
import { UnitMeasurementSystem } from '@/constants/enums';

import { RulesBehaviour } from './common';
import {
  EngineSubstepsEnum,
  FallbackBehaviour,
  DefaultBehaviour,
  scenarioGroupsEditableFields,
  ruleEditableFields,
} from './constants';
import { convertNullToInfinity, convertPressureToMetric } from './utils';

export class FallbackScenarios {
  @observable scenarios: Scenario[];
  @observable oldValues: Partial<FallbackScenarios>;
  @observable ignoreFallbackCompressors: string[] = [];

  constructor(fallbackScenarios?, tvalue?, unitMeasurementSystem?, ignoreFallbackCompressors?) {
    const oldFallbackScenarios = JSON.parse(
      window.localStorage.getItem(EngineSubstepsEnum.FallbackScenarios),
      convertNullToInfinity,
    );
    makeObservable(this);
    this.scenarios =
      fallbackScenarios?.scenarios?.map(
        (scenario, index) =>
          new Scenario(scenario, oldFallbackScenarios?.scenarios?.[index], tvalue, unitMeasurementSystem),
      ) ?? [];
    // Use ignoreFallbackCompressors if explicitly provided and not empty
    // Otherwise, try to get it from fallbackScenarios object
    // If passed value is empty array but fallbackScenarios has it, prefer the one from fallbackScenarios
    if (ignoreFallbackCompressors !== undefined && ignoreFallbackCompressors.length > 0) {
      this.ignoreFallbackCompressors = ignoreFallbackCompressors;
    } else {
      // Check fallbackScenarios object first, then the passed parameter, then default to empty array
      this.ignoreFallbackCompressors =
        fallbackScenarios?.ignoreFallbackCompressors ||
        (fallbackScenarios as any)?.additionalInfo?.fallback?.ignoreFallbackCompressors ||
        (ignoreFallbackCompressors !== undefined ? ignoreFallbackCompressors : []) ||
        [];
    }
    if (oldFallbackScenarios)
      this.oldValues = {
        scenarios: oldFallbackScenarios?.scenarios?.map(
          scenario => new Scenario(scenario, undefined, tvalue, unitMeasurementSystem),
        ),
        ignoreFallbackCompressors: oldFallbackScenarios?.ignoreFallbackCompressors || [],
      };
  }

  @action addScenario() {
    this.scenarios = [
      ...(this.scenarios ?? []),
      new Scenario({
        id: uuidv4(),
        name: `Scenario ${this.scenarioNameCounter() + 1}`,
      }),
    ];
  }

  @action setScenarioField(scenarioId, field, value, flowRangeKey?) {
    this.scenarios = this.scenarios?.map(scenario =>
      scenario?.id === scenarioId
        ? new Scenario({
            ...scenario,
            ...(field === 'defaultBehaviour' || field === 'ruleParentKey'
              ? {
                  fallbackRules: scenario?.fallbackRules?.map(fallbackRule =>
                    (flowRangeKey ? fallbackRule?.flowRangeKey === flowRangeKey : true)
                      ? { ...fallbackRule, [field]: value }
                      : fallbackRule,
                  ),
                }
              : { [field]: value }),
          })
        : scenario,
    );
  }

  @action deleteScenario(scenarioId) {
    this.scenarios = this.scenarios?.filter(scenario => scenario?.id !== scenarioId);
  }

  @action moveScenario(from, to) {
    this.scenarios?.splice(to, 0, this.scenarios?.splice(from, 1)[0]);
  }

  @action setIgnoreFallbackCompressors(compressorIds: string[]) {
    this.ignoreFallbackCompressors = Array.isArray(compressorIds) ? compressorIds : [];
  }

  @computed get hasRulesByFlowLevel() {
    return this.scenarios?.some(scenario => scenario?.fallbackBehaviour === FallbackBehaviour.LogicPerFlowLevel);
  }

  scenarioNameCounter() {
    const scenarioNameCounters = (this.scenarios ?? [])
      .map(scenario => scenario?.name?.substring(8))
      .filter(counter => !isNaN(+counter));
    const maxScenarioNameCounter = +max(scenarioNameCounters);
    return isNaN(maxScenarioNameCounter) ? 0 : maxScenarioNameCounter;
  }

  validate(args, focusedField?) {
    return this.scenarios?.reduce((result, current) => {
      const validationMessages = [];
      if (!current?.compressors?.length)
        validationMessages.push({
          key: current?.id,
          field: 'compressors',
          message: 'compressorsAreMandatory',
          focused: focusedField ? focusedField === `compressors-${current?.id}` : true,
          tab: EngineTab.FallbackScenarios,
        });
      if (
        this.scenarios.some(
          scenario => scenario?.id !== current?.id && isEqual(scenario?.compressors, current.compressors),
        )
      )
        validationMessages.push({
          key: current?.id,
          field: 'compressors',
          message: 'compressorsCombinationMustBeUnique',
          focused: focusedField ? focusedField === `compressors-${current?.id}` : true,
          tab: EngineTab.FallbackScenarios,
        });
      if (!current?.fallbackBehaviour)
        validationMessages.push({
          key: current?.id,
          field: 'fallbackBehaviour',
          message: 'oneFallbackOptionShouldBeSelected',
          focused: focusedField ? focusedField === `fallbackBehaviour-${current?.id}` : true,
          tab: EngineTab.FallbackScenarios,
        });
      if (
        current?.fallbackRules?.some(
          fallbackRule =>
            !fallbackRule?.defaultBehaviour ||
            (fallbackRule?.defaultBehaviour === DefaultBehaviour.ExistingRule && !fallbackRule?.ruleParentKey) ||
            (fallbackRule?.defaultBehaviour === DefaultBehaviour.CustomRule && !fallbackRule?.rules?.length),
        )
      )
        validationMessages.push({
          key: current?.id,
          field: 'fallbackBehaviour',
          message: 'oneRuleShouldBeSelected',
          focused: focusedField ? focusedField === `fallbackBehaviour-${current?.id}` : true,
          tab: EngineTab.FallbackScenarios,
        });
      return [...result, ...(validationMessages.length ? validationMessages : [])];
    }, []);
  }

  hasChanges() {
    if (this.oldValues?.scenarios?.length !== this.scenarios?.length) {
      return true;
    }

    // Check if ignoreFallbackCompressors has changed
    if (!isEqual(this.ignoreFallbackCompressors, this.oldValues?.ignoreFallbackCompressors)) {
      return true;
    }

    return (
      !isEmpty(this.oldValues) &&
      scenarioGroupsEditableFields.some(field =>
        this.scenarios?.some(scenario => {
          const oldScenario = find(this.oldValues?.scenarios, { id: scenario.id });
          if (field === 'fallbackRules') {
            return scenario?.fallbackRules?.some(fallbackRule => {
              const oldFallbackRule = find(oldScenario?.fallbackRules, { id: fallbackRule?.id });
              return (
                fallbackRule.defaultBehaviour !== oldFallbackRule?.defaultBehaviour ||
                fallbackRule.ruleParentKey !== oldFallbackRule?.ruleParentKey ||
                fallbackRule.rules?.some(rule =>
                  ['name', ...ruleEditableFields].some(
                    field => rule?.[field] !== find(oldFallbackRule?.rules, { key: rule.key })?.[field],
                  ),
                )
              );
            });
          }
          return !isEqual(scenario?.[field], oldScenario?.[field]);
        }),
      )
    );
  }

  @action cancelChanges() {
    this.scenarios = this.oldValues?.scenarios;
    this.ignoreFallbackCompressors = this.oldValues?.ignoreFallbackCompressors || [];
  }

  getValuesToSave(tvalue, unitMeasurementSystem) {
    const scenarios =
      unitMeasurementSystem === UnitMeasurementSystem.Imperial
        ? this.scenarios?.map(scenario => ({
            ...scenario,
            fallbackRules: scenario?.fallbackRules?.map(fallbackRule => ({
              ...fallbackRule.getValuesToSave(tvalue, unitMeasurementSystem),
              rules: fallbackRule?.rules?.map(rule => ({
                ...rule,
                load: convertPressureToMetric(rule?.load, tvalue),
                unload: convertPressureToMetric(rule?.unload, tvalue),
                autoStop: convertPressureToMetric(rule?.autoStop, tvalue),
                immediateStop: convertPressureToMetric(rule?.immediateStop, tvalue),
                vsdTarget: convertPressureToMetric(rule?.vsdTarget, tvalue),
              })),
            })),
          }))
        : this.scenarios;
    return omit({ ...this, scenarios }, 'oldValues', 'ignoreFallbackCompressors');
  }
}

export class Scenario {
  @observable id: string;
  @observable name: string;
  @observable compressors: string[];
  @observable fallbackBehaviour: FallbackBehaviour;
  @observable fallbackRules: FallbackRule[];

  constructor(scenario?, oldScenario?, tvalue?, unitMeasurementSystem?) {
    makeObservable(this);
    this.id = scenario?.id;
    this.name = scenario?.name;
    this.compressors = scenario?.compressors;
    this.fallbackBehaviour = scenario?.fallbackBehaviour;
    this.fallbackRules =
      scenario?.fallbackRules?.map(
        fallbackRule =>
          new FallbackRule(
            fallbackRule,
            find(oldScenario?.fallbackRules, { id: fallbackRule?.id }),
            tvalue,
            unitMeasurementSystem,
          ),
      ) ?? [];
  }
}

export class FallbackRule extends RulesBehaviour {
  @observable flowRangeKey: string;
  @observable flowRangeName: string;

  constructor(fallbackRule?, oldFallbackRule?, tvalue?, unitMeasurementSystem?) {
    super(fallbackRule, oldFallbackRule, tvalue, unitMeasurementSystem);
    makeObservable(this);
    this.flowRangeKey = fallbackRule?.flowRangeKey;
    this.flowRangeName = fallbackRule?.flowRangeName;
  }

  getValuesToSave(tvalue, unitMeasurementSystem) {
    return omit({ ...this, ...super.getValuesToSave(tvalue, unitMeasurementSystem) }, 'oldValues');
  }
}
